import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send, ShieldCheck, Trash2 } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { Badge, Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Field, RootError, Section, SecretInput, secretToSend } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { asRecord } from '@/lib/json';
import { tenantKeys, useChannels, type ChannelDto } from './api';
import {
  emailSchema,
  smsSchema,
  testSendSchema,
  whatsappSchema,
  type EmailForm,
  type SmsForm,
  type TestSendForm,
  type WhatsappForm,
} from './schemas';
import { brandingFromForm, brandingToForm, emailCredentials, healthVariant, smsCredentials } from './helpers';
import { TemplatesPanel } from './TemplatesPanel';

type HealthReport = components['schemas']['ChannelHealthReport'];
type Kind = 'whatsapp' | 'sms' | 'email';

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Invalidate the channels list (and templates) of one tenant. */
function useInvalidateChannels(tenantId: string) {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'channels') });
    await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'wa-templates') });
  };
}

export function ChannelsTab({ tenantId, canTestSend, locales }: { tenantId: string; canTestSend: boolean; locales: string[] }) {
  const channels = useChannels(tenantId);
  const byKind = (k: Kind) => channels.data?.items.find((c) => c.kind === k);
  const [testKind, setTestKind] = useState<Kind | null>(null);

  const wa = byKind('whatsapp');
  return (
    <div className="flex flex-col gap-8">
      <WhatsappCard tenantId={tenantId} channel={wa} onTestSend={canTestSend ? () => setTestKind('whatsapp') : undefined} />
      {wa && <TemplatesPanel tenantId={tenantId} locales={locales} />}
      <SmsCard tenantId={tenantId} channel={byKind('sms')} onTestSend={canTestSend ? () => setTestKind('sms') : undefined} />
      <EmailCard tenantId={tenantId} channel={byKind('email')} onTestSend={canTestSend ? () => setTestKind('email') : undefined} />
      {testKind && <TestSendDialog tenantId={tenantId} kind={testKind} onClose={() => setTestKind(null)} />}
    </div>
  );
}

function ChannelHeader({ channel, onTestSend }: { channel?: ChannelDto; onTestSend?: () => void }) {
  const { t } = useTranslation();
  if (!channel) return <Badge>{t('admin.channels.notConfigured')}</Badge>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={healthVariant(channel.health)} data-testid={`health-${channel.kind}`}>
        {t(`admin.channels.health.${channel.health}`)}
      </Badge>
      {channel.qualityRating && <Badge>{t('admin.channels.quality', { rating: channel.qualityRating })}</Badge>}
      {channel.messagingLimitTier && <Badge>{channel.messagingLimitTier}</Badge>}
      {onTestSend && (
        <Button size="sm" variant="outline" onClick={onTestSend}>
          <Send /> {t('admin.channels.testSend')}
        </Button>
      )}
    </div>
  );
}

function WhatsappCard({ tenantId, channel, onTestSend }: { tenantId: string; channel?: ChannelDto; onTestSend?: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateChannels(tenantId);
  const cfg = asRecord(channel?.config);
  const tokenConfigured = Boolean(channel?.secret);
  const [report, setReport] = useState<HealthReport | null>(null);

  const form = useForm<WhatsappForm>({
    resolver: zodResolver(whatsappSchema(t, tokenConfigured)),
    values: {
      phoneNumberId: str(cfg['phoneNumberId']),
      wabaId: str(cfg['wabaId']),
      displayPhone: str(cfg['displayPhone']),
      systemUserToken: '',
      appId: str(cfg['appId']),
      businessId: str(cfg['businessId']),
      templateNamespace: str(cfg['templateNamespace']),
    },
  });
  const save = useMutation({
    meta: { silent: true },
    mutationFn: async (v: WhatsappForm) =>
      unwrap(
        await api.PUT('/v1/tenants/{id}/channels/whatsapp', {
          params: { path: { id: tenantId } },
          body: {
            phoneNumberId: v.phoneNumberId,
            wabaId: v.wabaId,
            displayPhone: v.displayPhone,
            systemUserToken: secretToSend(v.systemUserToken) ?? null,
            appId: v.appId || null,
            businessId: v.businessId || null,
            templateNamespace: v.templateNamespace || null,
          },
        }),
      ),
    onSuccess: invalidate,
  });
  const verify = useMutation({
    mutationFn: async () => unwrap(await api.POST('/v1/tenants/{id}/channels/whatsapp/verify', { params: { path: { id: tenantId } } })),
    onSuccess: async (r) => {
      setReport(r);
      await invalidate();
    },
  });

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
      toast.success(t('admin.channels.whatsappSaved'));
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['phoneNumberId', 'wabaId', 'displayPhone', 'systemUserToken', 'appId', 'businessId', 'templateNamespace'] });
    }
  });
  const { errors, isSubmitting } = form.formState;

  return (
    <Section
      title={t('admin.channels.whatsapp')}
      description={t('admin.channels.whatsappDescription')}
      actions={
        <>
          <ChannelHeader channel={channel} onTestSend={channel ? onTestSend : undefined} />
          {channel && (
            <Button size="sm" variant="outline" disabled={verify.isPending} onClick={() => verify.mutate()}>
              <ShieldCheck /> {t('admin.channels.verify')}
            </Button>
          )}
        </>
      }
    >
      <Card className="p-4">
        <form className="grid grid-cols-2 gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.channels.phoneNumberId')} htmlFor="wa-pnid" error={errors.phoneNumberId?.message}>
            <Input id="wa-pnid" className="font-mono" aria-invalid={!!errors.phoneNumberId} {...form.register('phoneNumberId')} />
          </Field>
          <Field label={t('admin.channels.wabaId')} htmlFor="wa-waba" error={errors.wabaId?.message}>
            <Input id="wa-waba" className="font-mono" aria-invalid={!!errors.wabaId} {...form.register('wabaId')} />
          </Field>
          <Field label={t('admin.channels.displayPhone')} htmlFor="wa-phone" error={errors.displayPhone?.message}>
            <Input id="wa-phone" placeholder="+41…" aria-invalid={!!errors.displayPhone} {...form.register('displayPhone')} />
          </Field>
          <Field label={t('admin.channels.systemUserToken')} htmlFor="wa-token" error={errors.systemUserToken?.message} hint={t('admin.channels.tokenHint')}>
            <Controller
              control={form.control}
              name="systemUserToken"
              render={({ field }) => (
                <SecretInput id="wa-token" configured={tokenConfigured} value={field.value} onChange={field.onChange} invalid={!!errors.systemUserToken} />
              )}
            />
          </Field>
          <Field label={t('admin.channels.appId')} htmlFor="wa-app">
            <Input id="wa-app" className="font-mono" {...form.register('appId')} />
          </Field>
          <Field label={t('admin.channels.businessId')} htmlFor="wa-biz">
            <Input id="wa-biz" className="font-mono" {...form.register('businessId')} />
          </Field>
          <div className="col-span-2 flex flex-col gap-2">
            <RootError message={errors.root?.message} />
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {channel ? t('admin.channels.saveAndVerify') : t('admin.channels.connect')}
              </Button>
              <span className="text-xs text-muted-foreground">{t('admin.channels.saveHint')}</span>
            </div>
          </div>
        </form>
        {(report ?? channel?.healthDetail) && (
          <div role="status" className="mt-3 rounded-md border bg-muted/30 p-2 text-xs">
            {report ? (
              <>
                <Badge variant={healthVariant(report.health)}>{t(`admin.channels.health.${report.health}`)}</Badge>{' '}
                {report.verifiedName && <span className="font-medium">{report.verifiedName}</span>} {report.detail}
              </>
            ) : (
              channel?.healthDetail
            )}
          </div>
        )}
      </Card>
    </Section>
  );
}

function DeleteChannelButton({ tenantId, kind }: { tenantId: string; kind: Kind }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateChannels(tenantId);
  const del = useMutation({
    mutationFn: async () => unwrap(await api.DELETE('/v1/tenants/{id}/channels/{kind}', { params: { path: { id: tenantId, kind } } })),
    onSuccess: invalidate,
  });
  return (
    <Button
      size="sm"
      variant="ghost"
      disabled={del.isPending}
      onClick={() => {
        if (window.confirm(t('admin.channels.confirmDelete'))) del.mutate();
      }}
    >
      <Trash2 /> {t('admin.common.remove')}
    </Button>
  );
}

/** Re-checks the stored SMS/email credentials with the provider (POST …/channels/{kind}/verify). */
function VerifyChannelButton({ tenantId, kind }: { tenantId: string; kind: 'sms' | 'email' }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateChannels(tenantId);
  const verify = useMutation({
    mutationFn: async () => unwrap(await api.POST('/v1/tenants/{id}/channels/{kind}/verify', { params: { path: { id: tenantId, kind } } })),
    onSuccess: async (r: HealthReport) => {
      await invalidate();
      if (r.health === 'healthy') toast.success(t('admin.channels.verified', { detail: r.detail ?? '' }));
      else toast.error(t('admin.channels.verifyFailed', { detail: r.detail ?? '' }));
    },
  });
  return (
    <Button size="sm" variant="outline" disabled={verify.isPending} onClick={() => verify.mutate()}>
      <ShieldCheck /> {t('admin.channels.verify')}
    </Button>
  );
}

function SmsCard({ tenantId, channel, onTestSend }: { tenantId: string; channel?: ChannelDto; onTestSend?: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateChannels(tenantId);
  const cfg = asRecord(channel?.config);
  const configured = Boolean(channel?.secret);
  const form = useForm<SmsForm>({
    resolver: zodResolver(smsSchema(t, configured)),
    values: { provider: 'twilio', fromNumber: str(cfg['fromNumber']), senderId: str(cfg['senderId']), accountSid: '', authToken: '' },
  });
  const save = useMutation({
    meta: { silent: true },
    mutationFn: async (v: SmsForm) =>
      unwrap(
        await api.PUT('/v1/tenants/{id}/channels/sms', {
          params: { path: { id: tenantId } },
          body: { provider: v.provider, fromNumber: v.fromNumber, senderId: v.senderId.trim() || null, credentials: smsCredentials(v) },
        }),
      ),
    onSuccess: invalidate,
  });
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
      toast.success(t('admin.common.saved'));
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['provider', 'fromNumber', 'senderId', 'accountSid', 'authToken'], aliases: { credentials: 'authToken' } });
    }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <Section
      title={t('admin.channels.sms')}
      description={t('admin.channels.smsDescription')}
      actions={
        <>
          <ChannelHeader channel={channel} onTestSend={channel ? onTestSend : undefined} />
          {channel && <VerifyChannelButton tenantId={tenantId} kind="sms" />}
          {channel && <DeleteChannelButton tenantId={tenantId} kind="sms" />}
        </>
      }
    >
      <Card className="p-4">
        <form className="grid grid-cols-2 gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.channels.provider')} htmlFor="sms-prov">
            <NativeSelect id="sms-prov" {...form.register('provider')}>
              <option value="twilio">Twilio</option>
            </NativeSelect>
          </Field>
          <Field label={t('admin.channels.fromNumber')} htmlFor="sms-from" error={errors.fromNumber?.message}>
            <Input id="sms-from" placeholder="+41…" aria-invalid={!!errors.fromNumber} {...form.register('fromNumber')} />
          </Field>
          <Field label={t('admin.channels.senderId')} htmlFor="sms-sender" error={errors.senderId?.message} hint={t('admin.channels.senderIdHint')}>
            <Input id="sms-sender" maxLength={11} aria-invalid={!!errors.senderId} {...form.register('senderId')} />
          </Field>
          <div />
          <Field label={t('admin.channels.accountSid')} htmlFor="sms-sid" error={errors.accountSid?.message} hint={t('admin.channels.credentialsHint')}>
            <Input id="sms-sid" className="font-mono" autoComplete="off" placeholder={configured ? '****' : 'AC…'} aria-invalid={!!errors.accountSid} {...form.register('accountSid')} />
          </Field>
          <Field label={t('admin.channels.authToken')} htmlFor="sms-token" error={errors.authToken?.message}>
            <Controller
              control={form.control}
              name="authToken"
              render={({ field }) => <SecretInput id="sms-token" configured={configured} value={field.value} onChange={field.onChange} invalid={!!errors.authToken} />}
            />
          </Field>
          <div className="col-span-2 flex flex-col gap-2">
            <RootError message={errors.root?.message} />
            <div>
              <Button type="submit" disabled={isSubmitting}>
                {t('admin.channels.saveAndVerify')}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </Section>
  );
}

function EmailCard({ tenantId, channel, onTestSend }: { tenantId: string; channel?: ChannelDto; onTestSend?: () => void }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateChannels(tenantId);
  const cfg = asRecord(channel?.config);
  const configured = Boolean(channel?.secret);
  const form = useForm<EmailForm>({
    resolver: zodResolver(emailSchema(t, configured)),
    values: {
      provider: str(cfg['provider']) === 'ses' ? 'ses' : 'sendgrid',
      fromAddress: str(cfg['fromAddress']),
      fromName: str(cfg['fromName']),
      replyToDomain: str(cfg['replyToDomain']),
      apiKey: '',
      accessKeyId: '',
      secretAccessKey: '',
      region: '',
      ...brandingToForm(cfg['branding']),
    },
  });
  const provider = form.watch('provider');
  const save = useMutation({
    meta: { silent: true },
    mutationFn: async (v: EmailForm) =>
      unwrap(
        await api.PUT('/v1/tenants/{id}/channels/email', {
          params: { path: { id: tenantId } },
          body: {
            provider: v.provider,
            fromAddress: v.fromAddress,
            fromName: v.fromName,
            replyToDomain: v.replyToDomain,
            credentials: emailCredentials(v),
            branding: brandingFromForm(v) as never,
          },
        }),
      ),
    onSuccess: invalidate,
  });
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
      toast.success(t('admin.common.saved'));
    } catch (err) {
      applyProblemToForm(form, err, {
        fields: ['provider', 'fromAddress', 'fromName', 'replyToDomain', 'apiKey', 'accessKeyId', 'secretAccessKey', 'region'],
        aliases: { credentials: provider === 'sendgrid' ? 'apiKey' : 'secretAccessKey' },
      });
    }
  });
  const { errors, isSubmitting } = form.formState;
  const secret = (name: 'apiKey' | 'secretAccessKey', id: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => <SecretInput id={id} configured={configured} value={field.value} onChange={field.onChange} invalid={!!errors[name]} />}
    />
  );
  return (
    <Section
      title={t('admin.channels.email')}
      description={t('admin.channels.emailDescription')}
      actions={
        <>
          <ChannelHeader channel={channel} onTestSend={channel ? onTestSend : undefined} />
          {channel && <VerifyChannelButton tenantId={tenantId} kind="email" />}
          {channel && <DeleteChannelButton tenantId={tenantId} kind="email" />}
        </>
      }
    >
      <Card className="p-4">
        <form className="grid grid-cols-2 gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.channels.provider')} htmlFor="em-prov">
            <NativeSelect id="em-prov" {...form.register('provider')}>
              <option value="sendgrid">SendGrid</option>
              <option value="ses">Amazon SES</option>
            </NativeSelect>
          </Field>
          <Field label={t('admin.channels.fromAddress')} htmlFor="em-from" error={errors.fromAddress?.message}>
            <Input id="em-from" type="email" aria-invalid={!!errors.fromAddress} {...form.register('fromAddress')} />
          </Field>
          <Field label={t('admin.channels.fromName')} htmlFor="em-name" error={errors.fromName?.message}>
            <Input id="em-name" aria-invalid={!!errors.fromName} {...form.register('fromName')} />
          </Field>
          <Field label={t('admin.channels.replyToDomain')} htmlFor="em-reply" error={errors.replyToDomain?.message}>
            <Input id="em-reply" placeholder="reply.agentic.jobdesk.com" aria-invalid={!!errors.replyToDomain} {...form.register('replyToDomain')} />
          </Field>
          {provider === 'sendgrid' ? (
            <Field className="col-span-2" label={t('admin.channels.apiKey')} htmlFor="em-key" error={errors.apiKey?.message} hint={t('admin.channels.credentialsHint')}>
              {secret('apiKey', 'em-key')}
            </Field>
          ) : (
            <>
              <Field label={t('admin.channels.accessKeyId')} htmlFor="em-akid" error={errors.accessKeyId?.message} hint={t('admin.channels.credentialsHint')}>
                <Input id="em-akid" className="font-mono" autoComplete="off" placeholder={configured ? '****' : 'AKIA…'} aria-invalid={!!errors.accessKeyId} {...form.register('accessKeyId')} />
              </Field>
              <Field label={t('admin.channels.region')} htmlFor="em-region" error={errors.region?.message}>
                <Input id="em-region" placeholder="eu-central-1" aria-invalid={!!errors.region} {...form.register('region')} />
              </Field>
              <Field className="col-span-2" label={t('admin.channels.secretAccessKey')} htmlFor="em-secret" error={errors.secretAccessKey?.message}>
                {secret('secretAccessKey', 'em-secret')}
              </Field>
            </>
          )}
          <h4 className="col-span-2 mt-2 text-sm font-medium">{t('admin.channels.branding')}</h4>
          <Field label={t('admin.channels.logoUrl')} htmlFor="em-logo" error={errors.logoUrl?.message}>
            <Input id="em-logo" placeholder="https://…" aria-invalid={!!errors.logoUrl} {...form.register('logoUrl')} />
          </Field>
          <Field label={t('admin.channels.primaryColor')} htmlFor="em-color" error={errors.primaryColor?.message}>
            <Input id="em-color" placeholder="#1f6feb" aria-invalid={!!errors.primaryColor} {...form.register('primaryColor')} />
          </Field>
          <Field label={t('admin.channels.footerText')} htmlFor="em-footer" error={errors.footerText?.message}>
            <Input id="em-footer" aria-invalid={!!errors.footerText} {...form.register('footerText')} />
          </Field>
          <Field label={t('admin.channels.imprint')} htmlFor="em-imprint" error={errors.imprint?.message}>
            <Input id="em-imprint" aria-invalid={!!errors.imprint} {...form.register('imprint')} />
          </Field>
          <div className="col-span-2 flex flex-col gap-2">
            <RootError message={errors.root?.message} />
            <div>
              <Button type="submit" disabled={isSubmitting}>
                {t('admin.channels.saveAndVerify')}
              </Button>
            </div>
          </div>
        </form>
      </Card>
    </Section>
  );
}

function TestSendDialog({ tenantId, kind, onClose }: { tenantId: string; kind: Kind; onClose: () => void }) {
  const { t } = useTranslation();
  const form = useForm<TestSendForm>({ resolver: zodResolver(testSendSchema(t, kind)), defaultValues: { to: '', text: '' } });
  const [result, setResult] = useState<components['schemas']['TestSendResponse'] | null>(null);
  const send = useMutation({
    meta: { silent: true },
    mutationFn: async (v: TestSendForm) =>
      unwrap(await api.POST('/v1/tenants/{id}/channels/{kind}/test-send', { params: { path: { id: tenantId, kind } }, body: v })),
  });
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      setResult(await send.mutateAsync(v));
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['to', 'text'] });
    }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.channels.testSendTitle', { kind: t(`admin.channels.${kind}`) })}</DialogTitle>
        <DialogDescription>{t('admin.channels.testSendHint')}</DialogDescription>
        <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.channels.to')} htmlFor="ts-to" error={errors.to?.message}>
            <Input id="ts-to" aria-invalid={!!errors.to} {...form.register('to')} />
          </Field>
          <Field label={t('admin.channels.text')} htmlFor="ts-text" error={errors.text?.message}>
            <Input id="ts-text" aria-invalid={!!errors.text} {...form.register('text')} />
          </Field>
          <RootError message={errors.root?.message} />
          {result && (
            <p role="status" className="text-xs">
              {result.ok ? (
                <Badge variant="success">{t('admin.channels.testSent', { id: result.providerMessageId ?? '' })}</Badge>
              ) : (
                <Badge variant="destructive">{t('admin.channels.testFailed', { reason: result.failure ?? '', code: result.providerCode ?? '' })}</Badge>
              )}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.common.close')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t('admin.channels.send')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
